import React from 'react'
import PropTypes from 'prop-types'
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField
} from '@mui/material'
import { Delete, Link, Save, Clear } from '@mui/icons-material'
import { StatusChip } from '@components/atoms'
import styles from '../SettingsSOP.module.scss'

const GoogleDocsCard = ({
  isAdmin,
  campaigns,
  selectedCampaign,
  handleCampaignChange,
  googleDocsExists,
  googleDocsUrl,
  googleDocsUrlInput,
  setGoogleDocsUrlInput,
  googleDocsSaving,
  googleDocsDeleting,
  handleGoogleDocsSave,
  handleGoogleDocsDelete,
  handleGoogleDocsClear,
  handleGoogleDocsCancel,
  getCurrentCampaign
}) => {
  const isSaveEnabled =
    googleDocsUrlInput.trim() && googleDocsUrlInput !== googleDocsUrl && !googleDocsSaving

  return (
    <Box className='uploadForm'>
      <Card className={styles.sopCard}>
        <CardContent className={styles.sopCardContent}>
          {/* Admin campaign dropdown */}
          {isAdmin && (
            <Box mb={4}>
              <Typography variant='subtitle1' className={styles.sectionLabel}>
                Campaign Management
              </Typography>
              <FormControl fullWidth variant='outlined' size='small'>
                <InputLabel>Select campaign to manage Google Docs</InputLabel>
                <Select
                  value={selectedCampaign}
                  onChange={handleCampaignChange}
                  label='Select campaign to manage Google Docs'
                  className={styles.selectRounded}
                >
                  {campaigns.map((campaign) => (
                    <MenuItem key={campaign.original_name} value={campaign.original_name}>
                      {campaign.campaign_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}

          {/* Google Docs Status */}
          <Box
            display='flex'
            alignItems='center'
            justifyContent='space-between'
            className={styles.statusRow}
          >
            <Typography variant='h6' className={styles.sectionTitle}>
              Google Docs SOP Status
            </Typography>
            {googleDocsExists ? (
              <StatusChip label='Active' color='success' />
            ) : (
              <StatusChip label='Not Set' color='warning' />
            )}
          </Box>

          <Divider className={styles.divider} />

          <Box display='flex' alignItems='center' className={styles.infoRowSpacing}>
            <Link className={styles.iconLarge} />
            <Box>
              <Typography variant='body1' className={styles.fontMedium}>
                {googleDocsExists ? 'Google Docs URL Available' : 'No Google Docs URL Set'}
              </Typography>
              <Typography variant='body2' color='textSecondary'>
                {googleDocsExists
                  ? `Campaign: ${decodeURIComponent(getCurrentCampaign() || '')}`
                  : 'Set a Google Docs URL for text-based SOP access'}
              </Typography>
              {googleDocsExists && googleDocsUrl && (
                <Box display='flex' alignItems='center' className={styles.metaRow}>
                  <Link className={styles.iconSmall} />
                  <Typography
                    variant='caption'
                    color='textSecondary'
                    className={styles.urlTruncate}
                  >
                    {googleDocsUrl}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>

          {/* Google Docs URL Management */}
          <Box className={styles.uploadZone}>
            <Box>
              <Typography variant='h6' className={styles.uploadTitle}>
                {googleDocsExists ? 'Update Google Docs URL' : 'Set Google Docs URL'}
              </Typography>
              <Typography
                variant='body2'
                color='textSecondary'
                className={styles.uploadDescription}
              >
                Enter a Google Docs document URL to enable text-based SOP access for AI agents
              </Typography>

              <TextField
                fullWidth
                variant='outlined'
                label='Google Docs URL'
                placeholder='https://docs.google.com/document/d/...'
                value={googleDocsUrlInput}
                onChange={(e) => setGoogleDocsUrlInput(e.target.value)}
                disabled={googleDocsSaving || googleDocsDeleting}
                className={styles.urlTextField}
                InputProps={{
                  startAdornment: (
                    <Link style={{ color: 'var(--color-text-secondary)', marginRight: '8px' }} />
                  )
                }}
              />

              <Box display='flex' flexWrap='wrap' alignItems='center' className={styles.buttonRow}>
                {/* Save Button */}
                <Button
                  variant='contained'
                  onClick={handleGoogleDocsSave}
                  disabled={
                    !googleDocsUrlInput.trim() ||
                    googleDocsSaving ||
                    googleDocsDeleting ||
                    googleDocsUrlInput === googleDocsUrl
                  }
                  className={styles.saveButton}
                  style={!isSaveEnabled ? { backgroundColor: '#ccc' } : undefined}
                  startIcon={
                    googleDocsSaving ? <div style={{ width: 16, height: 16 }} /> : <Save />
                  }
                >
                  {googleDocsSaving ? 'Saving...' : googleDocsExists ? 'Update' : 'Save'}
                </Button>

                {/* Clear Button */}
                <Button
                  variant='outlined'
                  onClick={handleGoogleDocsClear}
                  disabled={!googleDocsUrlInput.trim() || googleDocsSaving || googleDocsDeleting}
                  className={styles.secondaryButton}
                  startIcon={<Clear />}
                >
                  Clear
                </Button>

                {/* Cancel Button (only show if there are unsaved changes) */}
                {googleDocsUrlInput !== googleDocsUrl && (
                  <Button
                    variant='outlined'
                    onClick={handleGoogleDocsCancel}
                    disabled={googleDocsSaving || googleDocsDeleting}
                    className={styles.secondaryButton}
                  >
                    Cancel
                  </Button>
                )}
              </Box>

              {/* Separate row for destructive actions */}
              {googleDocsExists && (
                <Box display='flex' alignItems='center' className={styles.destructiveRow}>
                  <Button
                    variant='outlined'
                    onClick={handleGoogleDocsDelete}
                    disabled={googleDocsDeleting || googleDocsSaving}
                    className={styles.deleteButton}
                    startIcon={<Delete />}
                  >
                    {googleDocsDeleting ? 'Removing...' : 'Remove URL'}
                  </Button>

                  <Button
                    variant='outlined'
                    onClick={() => window.open(googleDocsUrl, '_blank')}
                    className={styles.viewButton}
                    startIcon={<Link />}
                  >
                    Open Document
                  </Button>
                </Box>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  )
}

GoogleDocsCard.propTypes = {
  isAdmin: PropTypes.bool.isRequired,
  campaigns: PropTypes.arrayOf(
    PropTypes.shape({
      original_name: PropTypes.string.isRequired,
      campaign_name: PropTypes.string.isRequired
    })
  ).isRequired,
  selectedCampaign: PropTypes.string.isRequired,
  handleCampaignChange: PropTypes.func.isRequired,
  googleDocsExists: PropTypes.bool.isRequired,
  googleDocsUrl: PropTypes.string.isRequired,
  googleDocsUrlInput: PropTypes.string.isRequired,
  setGoogleDocsUrlInput: PropTypes.func.isRequired,
  googleDocsSaving: PropTypes.bool.isRequired,
  googleDocsDeleting: PropTypes.bool.isRequired,
  handleGoogleDocsSave: PropTypes.func.isRequired,
  handleGoogleDocsDelete: PropTypes.func.isRequired,
  handleGoogleDocsClear: PropTypes.func.isRequired,
  handleGoogleDocsCancel: PropTypes.func.isRequired,
  getCurrentCampaign: PropTypes.func.isRequired
}

export default GoogleDocsCard
